import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { productsClient } from 'apollo';
import { useQuery } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import BaseModal from 'components/Modal';
import {
  GetStoresQuery as StoresResult,
  GetStoresQueryVariables as StoresVars,
  GetStoresDocument as StoresQuery,
  GetProductsQuery as ProductsResult,
  GetProductsQueryVariables as ProductsVars,
  GetProductsDocument as ProductsQuery,
} from 'generated/products-graphql';

import { getMenuFilters } from 'components/ProductsSelection/utils';

import { HeliosProduct, Store } from 'generated/products-graphql';
import { ProductType, ProductSelection } from 'components/ProductsSelection/types';
import { FilterSelectionType, FilterType, SelectionType } from 'utils/types';

import * as filtersSelectors from 'components/FilterModal/store/selectors';
import * as filterActions from 'components/FilterModal/store/actions';
import BodyFilterModal from 'components/FilterModal';

export type PreloadedType = { [key in SelectionType]: boolean };

const MENU_SECTIONS: Array<ProductType> = ['accounts', 'states', 'stores', 'items'];

interface Props {
  clientId: string;
  currentFilter: FilterType;
  show: boolean;
  weeks?: number;
  brand?: string;
  itemsFromFilter?: Array<HeliosProduct>;
  storesFromFilter?: Array<Store>;
  onClose: any;
  selection: ProductSelection;
  clearSelections: () => void;
  initFilterValues: (values: FilterSelectionType) => void;
  onFilterChange(newFilter: FilterSelectionType): void;
}

const FilterModal: React.FC<Props> = ({
  clientId,
  currentFilter,
  show,
  brand,
  onClose,
  selection,
  clearSelections,
  initFilterValues,
  onFilterChange,
}) => {
  useEffect(() => {
    initFilterValues(currentFilter);
  }, [initFilterValues, currentFilter]);

  const { data: stores, loading: storesLoading } = useQuery<StoresResult, StoresVars>(StoresQuery, {
    client: productsClient as any,
    variables: {
      clientID: clientId,
      brand: brand ? brand : '',
      stores: currentFilter.stores && currentFilter.stores ? currentFilter.stores : [],
      pageable: {
        page: 1,
        pageSize:
          currentFilter.stores && currentFilter.stores.length ? currentFilter.stores.length : 10,
      },
    },
    skip: currentFilter.stores?.length === 0,
    fetchPolicy: 'network-only',
  });

  const { data: products, loading: productsLoading } = useQuery<ProductsResult, ProductsVars>(
    ProductsQuery,
    {
      client: productsClient as any,
      variables: {
        clientID: clientId,
        brand: brand ? brand : '',
        products: currentFilter.items && currentFilter.items.length ? currentFilter.items : [],
        pageable: {
          page: 1,
          pageSize:
            currentFilter.items && currentFilter.items.length ? currentFilter.items.length : 10,
        },
      },
      skip: currentFilter.items && currentFilter.items.length === 0,
      fetchPolicy: 'network-only',
    }
  );

  const storesFromFilters = stores?.stores?.data ? stores?.stores?.data : [];
  const productsFromFilters = products?.products?.data ? products?.products?.data : [];

  const handleClearSelections = () => {
    clearSelections();
  };

  const handleOk = () => {
    onFilterChange(getMenuFilters(MENU_SECTIONS, selection, null));
    handleClearSelections();
  };

  const handleClose = () => {
    onClose();
    handleClearSelections();
  };

  return (
    <BaseModal
      buttons={[
        <Button variant="light" onClick={handleClose}>
          Cancel
        </Button>,
        <Button onClick={handleOk}>Continue</Button>,
      ]}
      show={show}
      size="xl"
      title="Filter Results"
      onClose={handleClose}
      loading={storesLoading || productsLoading}
    >
      <BodyFilterModal
        typeOfFilter="full"
        storesFromEntity={storesFromFilters}
        productsFromEntity={productsFromFilters}
      />
    </BaseModal>
  );
};

const mapStateToProps = (state) => ({
  selection: filtersSelectors.selectionSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  clearSelections: () => {
    dispatch(filterActions.clearSelections());
  },
  initFilterValues: (values: FilterSelectionType) => {
    dispatch(filterActions.initValues(values));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterModal);
